import { useState, useEffect } from 'react';
import {
  File,
  Folder,
  ChevronRight,
  ChevronDown,
  FileCode,
  FileJson,
  FileText,
  FolderOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGitStore } from '@/store/gitStore';
import type { RepoFile } from '@/types/git';

interface FileExplorerProps {
  className?: string;
  onFileSelect: ((file: RepoFile) => void) | undefined;
  selectedPath: string | undefined;
}

/**
 * Компонент для отображения файлов проекта
 * Показывает дерево файлов с иконками и статусами
 */
export function FileExplorer({ className, onFileSelect, selectedPath }: FileExplorerProps) {
  const { repo } = useGitStore();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['src']));

  // Логируем состояние файлов для отладки
  useEffect(() => {
    console.log('[FileExplorer] repo.files:', repo.files?.length ?? 0, repo.files);
  }, [repo.files]);

  // Группируем файлы по директориям
  const files = repo.files ?? [];
  const fileTree = buildFileTree(files);

  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  return (
    <div className={cn('file-explorer flex flex-col h-full', className)}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-slate-900/50">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
          Project Files
        </span>
        <span className="text-xs text-slate-500">{files.length} files</span>
      </div>

      <div className="flex-1 overflow-auto py-1">
        {files.length === 0 ? (
          <div className="px-3 py-4 text-sm text-slate-500 text-center">No files in repository</div>
        ) : (
          <div className="space-y-0.5">
            {Object.entries(fileTree).map(([name, node]) => (
              <TreeNode
                key={name}
                name={name}
                node={node}
                path={name}
                depth={0}
                expandedFolders={expandedFolders}
                selectedPath={selectedPath}
                onToggleFolder={toggleFolder}
                onFileSelect={onFileSelect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface TreeNodeProps {
  name: string;
  node: FileNode;
  path: string;
  depth: number;
  expandedFolders: Set<string>;
  selectedPath: string | undefined;
  onToggleFolder: (path: string) => void;
  onFileSelect: ((file: RepoFile) => void) | undefined;
}

function TreeNode({
  name,
  node,
  path,
  depth,
  expandedFolders,
  selectedPath,
  onToggleFolder,
  onFileSelect,
}: TreeNodeProps) {
  const isExpanded = expandedFolders.has(path);
  const isFolder = node.type === 'folder';
  const isSelected = path === selectedPath;

  const handleClick = () => {
    if (isFolder) {
      onToggleFolder(path);
    } else if (node.file && onFileSelect) {
      onFileSelect(node.file);
    }
  };

  const Icon = isFolder ? (isExpanded ? FolderOpen : Folder) : getFileIcon(name);

  return (
    <>
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 cursor-pointer hover:bg-slate-800/50 transition-colors',
          isSelected && 'bg-slate-700/50',
          'group'
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={handleClick}
      >
        {isFolder && (
          <span className="text-slate-500">
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </span>
        )}

        <Icon className={cn('h-4 w-4', isFolder ? 'text-amber-500' : getFileColor(name))} />

        <span
          className={cn(
            'text-sm truncate',
            isFolder ? 'text-slate-300 font-medium' : 'text-slate-400'
          )}
        >
          {name}
        </span>

        {node.file && node.file.status !== 'committed' && (
          <span
            className={cn(
              'ml-auto text-xs px-1.5 py-0.5 rounded',
              node.file.status === 'modified' && 'bg-amber-500/20 text-amber-400',
              node.file.status === 'staged' && 'bg-green-500/20 text-green-400',
              node.file.status === 'untracked' && 'bg-slate-500/20 text-slate-400'
            )}
          >
            {node.file.status}
          </span>
        )}
      </div>

      {isFolder && isExpanded && node.children && (
        <>
          {Object.entries(node.children).map(([childName, childNode]) => (
            <TreeNode
              key={`${path}/${childName}`}
              name={childName}
              node={childNode}
              path={`${path}/${childName}`}
              depth={depth + 1}
              expandedFolders={expandedFolders}
              selectedPath={selectedPath}
              onToggleFolder={onToggleFolder}
              onFileSelect={onFileSelect}
            />
          ))}
        </>
      )}
    </>
  );
}

interface FileNode {
  type: 'file' | 'folder';
  file: RepoFile | undefined;
  children: Record<string, FileNode> | undefined;
}

function buildFileTree(files: RepoFile[]): Record<string, FileNode> {
  const root: Record<string, FileNode> = {};

  for (const file of files) {
    const parts = file.path.split('/');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue; // Пропускаем пустые части пути

      const isLast = i === parts.length - 1;

      if (!current[part]) {
        current[part] = {
          type: isLast ? 'file' : 'folder',
          file: isLast ? file : undefined,
          children: isLast ? undefined : {},
        };
      }

      if (!isLast && current[part]?.children) {
        current = current[part].children;
      }
    }
  }

  return root;
}

function getFileIcon(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase();

  switch (ext) {
    case 'ts':
    case 'tsx':
    case 'js':
    case 'jsx':
      return FileCode;
    case 'json':
      return FileJson;
    case 'md':
    case 'txt':
      return FileText;
    default:
      return File;
  }
}

function getFileColor(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase();

  switch (ext) {
    case 'ts':
    case 'tsx':
      return 'text-blue-400';
    case 'js':
    case 'jsx':
      return 'text-yellow-400';
    case 'json':
      return 'text-green-400';
    case 'md':
      return 'text-slate-400';
    case 'css':
      return 'text-pink-400';
    default:
      return 'text-slate-400';
  }
}
